import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControlLabel,
  FormHelperText,
  Typography,
  Tabs,
  Tab,
  Stack,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { IconPencil } from '@tabler/icons-react';
import { useForm, Controller } from 'react-hook-form';

import { useStyles } from '../styles';
import {
  SpeechPacingConfigDto,
  TurnHandlingConfigDto,
  useAgentProfileEditControllerAgentProfileEditForUsersMutation,
} from '@shared/services/apiService';
import { MarkdownEditor, MarkdownContent } from '@shared/components/MarkdownEditor';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { useErrorHandler } from '@shared/hooks/useErrorHandler';
import CustomSwitch from '@shared/components/forms/theme-elements/CustomSwitch';
import { TurnHandlingFields } from './TurnHandlingFields';
import { SpeechPacingFields } from './SpeechPacingFields';
import {
  cleanSpeechPacing,
  cleanTurnHandling,
  SPEECH_PACING_DEFAULTS,
  TURN_HANDLING_DEFAULTS,
} from '../utils';

type Mode = 'view' | 'edit';

interface AgentProfileModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  profile: any;
  initialMode?: Mode;
}

interface FormData {
  name: string;
  prompt: string;
  firstMessage: string;
  firstMessageInterruptionsEnabled: boolean;
  turnHandling?: TurnHandlingConfigDto;
  speechPacing?: SpeechPacingConfigDto;
}

const DEFAULT_VALUES: FormData = {
  name: '',
  prompt: '',
  firstMessage: '',
  firstMessageInterruptionsEnabled: false,
  turnHandling: undefined,
  speechPacing: undefined,
};

export const AgentProfileModal: React.FC<AgentProfileModalProps> = ({
  open,
  onClose,
  onSuccess,
  profile,
  initialMode = 'view',
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const { showError } = useErrorHandler();

  const [mode, setMode] = useState<Mode>(initialMode);
  const [tab, setTab] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [editAgentProfile] = useAgentProfileEditControllerAgentProfileEditForUsersMutation();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<FormData>({ defaultValues: DEFAULT_VALUES, mode: 'onChange' });

  const readOnly = mode === 'view';

  // react-markdown-editor-lite needs a pixel height. Measure the available area
  // on the General tab in edit mode and size the editor to fill it, so the
  // editor owns the only scroll on that tab.
  const promptAreaRef = useRef<HTMLDivElement | null>(null);
  const [promptEditorHeight, setPromptEditorHeight] = useState(320);
  useEffect(() => {
    if (readOnly || tab !== 0 || !open) return undefined;
    const el = promptAreaRef.current;
    if (!el) return undefined;
    const update = () => setPromptEditorHeight(Math.max(180, Math.floor(el.clientHeight) - 24));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [readOnly, tab, open]);

  const resetFromProfile = () => {
    reset({
      name: profile?.name ?? '',
      prompt: profile?.prompt ?? '',
      firstMessage: profile?.firstMessage ?? '',
      firstMessageInterruptionsEnabled: profile?.firstMessageInterruptionsEnabled ?? false,
      turnHandling: profile?.turnHandling ?? undefined,
      speechPacing: profile?.speechPacing ?? undefined,
    });
  };

  useEffect(() => {
    if (open) {
      setMode(initialMode);
      setTab(0);
      resetFromProfile();
    }
  }, [open, profile, initialMode]);

  const onSubmit = async (data: FormData) => {
    if (!profile?.id) return;

    try {
      setIsSubmitting(true);
      const normalizedFirstMessage = data.firstMessage.trim();

      await editAgentProfile({
        profileId: profile.id,
        agentProfileEditRequestDto: {
          name: data.name,
          prompt: data.prompt,
          firstMessage: normalizedFirstMessage === '' ? null : normalizedFirstMessage,
          firstMessageInterruptionsEnabled: data.firstMessageInterruptionsEnabled,
          turnHandling: cleanTurnHandling(data.turnHandling) ?? null,
          speechPacing: cleanSpeechPacing(data.speechPacing) ?? null,
        },
      }).unwrap();

      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
        data: {
          title: t('assistantsPage.success.title'),
          description:
            t('assistantsPage.success.updated', { name: data.name }) ||
            `Agent profile "${data.name}" updated successfully`,
        },
      });

      onSuccess();
    } catch (error) {
      showError({
        title: t('assistantsPage.errors.updateTitle'),
        error,
        suggestion: t('assistantsPage.errors.updateSuggestion'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    reset(DEFAULT_VALUES);
    onClose();
  };

  const handleCancelEdit = () => {
    if (initialMode === 'view') {
      resetFromProfile();
      setMode('view');
    } else {
      handleClose();
    }
  };

  if (!profile) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={readOnly ? 'lg' : false}
      fullWidth
      scroll="paper"
      PaperProps={{ className: classes.modalPaper }}
      // Edit mode gets a near-fullscreen dialog for a roomier editor; view stays
      // compact. Targeting the paper here overrides the class maxHeight.
      sx={
        readOnly
          ? undefined
          : {
              '& .MuiDialog-paper': {
                width: '96vw',
                maxWidth: '96vw',
                height: '96vh',
                maxHeight: '96vh',
                m: 0,
              },
            }
      }>
      <DialogTitle sx={{ flexShrink: 0, pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight={600}>
            {readOnly
              ? t('assistantsPage.modals.view.title')
              : t('assistantsPage.modals.edit.title')}
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            aria-label={t('assistantsPage.modals.close')}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        className={classes.modalForm}
        sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ px: 3, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Tab label={t('assistantsPage.modals.tabs.general')} />
          <Tab label={t('assistantsPage.modals.tabs.turnPacing')} />
        </Tabs>

        <DialogContent
          dividers
          sx={{
            p: 0,
            flex: '1 1 auto',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
          {/* General tab — in edit mode the markdown editor fills and owns the
              scroll; in view mode the panel itself scrolls. */}
          <Box
            sx={{
              display: tab === 0 ? 'flex' : 'none',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
              overflowY: readOnly ? 'auto' : 'hidden',
              p: 3,
              pt: 2,
            }}>
            <Box className={classes.formField}>
              <Controller
                name="name"
                control={control}
                rules={readOnly ? {} : { required: t('assistantsPage.modals.validation.name') }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('assistantsPage.modals.edit.nameLabel')}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    variant="outlined"
                    slotProps={{ input: { readOnly } }}
                    className={cx(classes.filledField, readOnly && classes.readOnlyField)}
                  />
                )}
              />
            </Box>

            {/* Opening line — placed second, right after the name */}
            <Box className={classes.formField}>
              <Controller
                name="firstMessage"
                control={control}
                render={({ field }) => (
                  <>
                    <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                      {t('assistantsPage.modals.openingLine.label')}
                    </Typography>
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      minRows={2}
                      placeholder={t('assistantsPage.modals.openingLine.placeholder')}
                      variant="outlined"
                      slotProps={{ input: { readOnly } }}
                      className={cx(classes.filledField, readOnly && classes.readOnlyField)}
                    />
                  </>
                )}
              />
            </Box>

            <Box className={classes.formField}>
              <Controller
                name="firstMessageInterruptionsEnabled"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    sx={{ ml: 0 }}
                    control={
                      <CustomSwitch
                        sx={{ ml: '20px', mr: '14px' }}
                        checked={!!field.value}
                        disabled={readOnly}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          field.onChange(e.target.checked)
                        }
                      />
                    }
                    label={t('assistantsPage.modals.firstMessageInterruptionsEnabled.label')}
                  />
                )}
              />
            </Box>

            {readOnly ? (
              <Box className={classes.formField}>
                <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                  {t('assistantsPage.modals.edit.promptLabel')}
                </Typography>
                {profile.prompt ? (
                  <Box className={classes.promptBox}>
                    <MarkdownContent content={profile.prompt} variant="embedded" sx={{ p: 2 }} />
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('assistantsPage.modals.view.noPrompt')}
                  </Typography>
                )}
              </Box>
            ) : (
              // Edit: the markdown editor fills the remaining height and owns the
              // vertical scroll, so the General tab has a single scroll area.
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', mt: 1 }}>
                <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                  {t('assistantsPage.modals.edit.promptLabel')} *
                </Typography>
                <Box ref={promptAreaRef} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                  <Controller
                    name="prompt"
                    control={control}
                    rules={{ required: t('assistantsPage.modals.validation.prompt') }}
                    render={({ field }) => (
                      <MarkdownEditor
                        value={field.value}
                        onChange={field.onChange}
                        error={!!errors.prompt}
                        height={promptEditorHeight}
                      />
                    )}
                  />
                </Box>
                {errors.prompt && (
                  <FormHelperText error sx={{ mt: 0.5 }}>
                    {errors.prompt.message as string}
                  </FormHelperText>
                )}
              </Box>
            )}
          </Box>

          {/* Turn & Pacing tab — scrolls within the dialog body. */}
          <Box
            sx={{
              display: tab === 1 ? 'block' : 'none',
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              p: 3,
              pt: 2,
            }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              {t('assistantsPage.modals.turnHandling.title')}
            </Typography>
            <TurnHandlingFields
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              control={control as any}
              disabled={readOnly}
              collapsible={false}
              onApplyDefaults={
                readOnly
                  ? undefined
                  : () =>
                      setValue('turnHandling', TURN_HANDLING_DEFAULTS, {
                        shouldDirty: true,
                        shouldValidate: true,
                      })
              }
            />

            <Box sx={{ height: 24 }} />

            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              {t('assistantsPage.modals.speechPacing.title')}
            </Typography>
            <SpeechPacingFields
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              control={control as any}
              disabled={readOnly}
              collapsible={false}
              onApplyDefaults={
                readOnly
                  ? undefined
                  : () =>
                      setValue('speechPacing', SPEECH_PACING_DEFAULTS, {
                        shouldDirty: true,
                        shouldValidate: true,
                      })
              }
            />
          </Box>
        </DialogContent>

        <DialogActions className={classes.modalActions} sx={{ flexShrink: 0 }}>
          {readOnly ? (
            <>
              <Button onClick={handleClose}>{t('assistantsPage.modals.close')}</Button>
              <Button
                type="button"
                variant="contained"
                startIcon={<IconPencil size={18} />}
                onClick={() => setMode('edit')}>
                {t('assistantsPage.modals.edit.title')}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleCancelEdit} disabled={isSubmitting}>
                {t('assistantsPage.modals.cancel')}
              </Button>
              <Button type="submit" variant="contained" disabled={isSubmitting}>
                {isSubmitting
                  ? t('assistantsPage.modals.updating')
                  : t('assistantsPage.modals.update')}
              </Button>
            </>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
};
